import { Box, Stack, styled } from "@mui/material"
import { type ComponentProps, Fragment } from "react"
import type { LaboratorySplit } from "../../../utils"
import type { DoughnutProps } from "./Doughnut"

type MarketShareDoughnutTooltipProps = NonNullable<
  DoughnutProps<LaboratorySplit>["CustomTooltip"]
>

type DoughnutDatum = ComponentProps<MarketShareDoughnutTooltipProps>["datum"]

export const MarketShareDoughnutTooltip: MarketShareDoughnutTooltipProps = ({
  datum,
}) => {
  if (datum.data.laboratories.length > 1) {
    return <MultipleDetailsTooltip {...datum} />
  }

  return <SingleDetailTooltip {...datum} />
}

const SingleDetailTooltip = ({ data }: DoughnutDatum) => {
  if (data.laboratories.length === 0) {
    return null
  }

  const detail = data.laboratories[0]
  return (
    <TooltipContainer>
      <TooltipColorBox color={data.color} />
      <Stack gap={0.5}>
        <span>
          {detail.name} : {detail.formattedPercentage}
        </span>
        <strong>{detail.formattedMainStat}</strong>
      </Stack>
    </TooltipContainer>
  )
}

const MultipleDetailsTooltip = ({ data }: DoughnutDatum) => (
  <TooltipContainer sx={{ flexDirection: "column", alignItems: "flex-start" }}>
    <Stack direction="row" gap={1} alignItems="center">
      <TooltipColorBox color={data.color} />
      <Box component="span" fontWeight={500}>
        {data.label} :
      </Box>
    </Stack>
    <DetailsGrid>
      {data.laboratories.map((detail) => (
        <Fragment key={detail.name}>
          <Box component="span">{detail.name} : </Box>
          <PercentageWithSeparator>
            {detail.formattedPercentage}
          </PercentageWithSeparator>
          <Box component="strong">{detail.formattedMainStat}</Box>
        </Fragment>
      ))}
    </DetailsGrid>
  </TooltipContainer>
)

const TooltipContainer = styled(Box)(
  ({ theme: { palette, typography, spacing } }) => ({
    backgroundColor: palette.common.white,
    color: palette.primary.dark1,
    fontSize: typography.pxToRem(12),
    lineHeight: typography.pxToRem(12),
    padding: spacing(1),
    borderRadius: spacing(1),
    boxShadow: "rgba(0, 0, 0, 0.25) 0px 0px 4px",
    whiteSpace: "pre",
    display: "flex",
    alignItems: "center",
    gap: spacing(1),
    textAlign: "left",
  }),
)

const TooltipColorBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "color",
})<{ color: string }>(({ color }) => ({
  height: "12px",
  width: "12px",
  backgroundColor: color,
}))

const DetailsGrid = styled(Box)(({ theme: { spacing } }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  columnGap: spacing(1),
  rowGap: spacing(1),
}))

const PercentageWithSeparator = styled("strong")(
  ({ theme: { palette, spacing } }) => ({
    borderRight: `1px solid ${palette.grey.extraLight}`,
    paddingLeft: spacing(0.5),
    paddingRight: spacing(0.5),
  }),
)
